import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { Tooltip } from '@components/tooltip';
import {
  CloudDownloadIcon,
  CommentIcon,
  DeleteIcon,
  DuplicateIcon,
  EditIcon,
  ReloadIcon,
  RightArrowIcon,
  // StopCircle,
  SubtaskIcon,
  TimeHourglassIcon,
  // TrackedTimeClockIcon,
} from '@sketchnote/icons';
import {
  Avatar,
  AvatarGroup,
  CheckboxKanbanItem,
  CheckboxMarkComplete,
  LoaderXs,
  MoreActionMenu,
  renderPriorityBars,
} from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
// import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { format, isPast, isSameDay, isToday, parseISO } from 'date-fns';
import { castArray, isArray } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Img } from 'react-image';
// import { Node } from 'slate';
import Analytics from 'src/api/services/analytics/analytics';
import { useTimerWidgetStore } from 'src/components/navbar/TimerWidget/useTimerWidgetStore';
import { useBoardStore } from 'src/features/boards';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useAgileStore } from 'src/features/scrum/store';
import {
  ApprovalMessage,
  ApprovalTagColor,
} from 'src/features/tasks/components/ApprovalComponents';
import { getFormattedValue } from 'src/utils/getFormattedValue';
// import { calculateTaskTime, TimerTracker } from 'src/features/tasks/components/TimeTrackerDetails';
import { shallow } from 'zustand/shallow';

import { useTasksStore } from '../../../tasks/store/useTasksStore';
import { useKanbanStore } from '../../store';
import { KanbanState } from '../../store/types';

const { useMemo, useCallback, useEffect, useState } = React;

export interface KanbanItemProps {
  columnId: string;
  itemIndex: number;
}

const getComment = (comment) => {
  if (isArray(comment)) return comment.length;
  return comment;
};

export const KanbanItem_new = ({ columnId, itemIndex }: KanbanItemProps) => {
  const {
    columns,
    onChangeCurrentSelectedItem,
    onDeleteItem,
    onDuplicateTask,
    onMarkAsComplete,
    isLoading,
  } = useKanbanStore(stateSelector, shallow);

  const boardData = useBoardStore();
  const router = useRouter();
  const templateId = router.query.templateId as string;
  const {
    isOpen: isMoreMenuOpened,
    onOpen: onOpenMoreMenu,
    onClose: onCloseMoreMenu,
  } = useDisclosure();
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const [isDuplicatePromptOpen, setIsDuplicatePromptOpen] = useState<boolean>(false);
  const [coverImage, setCoverImage] = useState<string>();
  const [isMouseOver, setMouseOver] = useState<boolean>(true);
  const onMarkAsCompleteSubTask = useTasksStore((s) => s.onMarkAsCompleteSubTask);
  const [openSnackbar] = useSnackbar();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const item = useMemo(
    () => columns.find((obj) => obj.id === columnId)?.items[itemIndex],
    [columns, columnId, itemIndex]
  );
  const { fetchTimers } = useTimerWidgetStore((s) => ({
    fetchTimers: s.fetchTimers,
  }));

  const isSelected = useMemo(() => {
    return !!boardData.selectedItems.find((i) => i === item?.id);
  }, [boardData.selectedItems]);

  const { isReadOnly } = useFileAccess();

  const { epics } = useAgileStore();

  const epicLabel = (epic) => {
    return (
      <button className="flex justify-between w-full ">
        <span
          className="leading-10 px-6 py-4 text-xs font-semibold text-white rounded-sm block mr-4 max-w-64 truncate"
          style={{ backgroundColor: epic.name[0].children[0].color }}
        >
          {epic.name[0].children[0].text}
        </span>
      </button>
    );
  };

  if (!item) return <></>;
  const hasEndDatePassed = useMemo(() => {
    const isLastColumn = columns.some(
      (col, colIndex) => col.id === columnId && colIndex === columns.length - 1
    );
    if (isLastColumn) return false;
    if (item.assignDate?.length === 1 && item.assignDate?.[0]) {
      const endDate = parseISO(item.assignDate[0]);
      if (isPast(endDate)) {
        return true;
      }
    }
    if (item.assignDate?.length === 2 && item.assignDate?.[0] && item.assignDate?.[1]) {
      const endDate = parseISO(item.assignDate[1]);
      if (isPast(endDate)) {
        return true;
      }
    }
    return false;
  }, [item.assignDate]);

  const isEndDateToday = useMemo(() => {
    const isLastColumn = columns.some(
      (col, colIndex) => col.id === columnId && colIndex === columns.length - 1
    );
    if (isLastColumn) return false;
    if (item.assignDate?.length === 1 && item.assignDate?.[0]) {
      const endDate = parseISO(item.assignDate[0]);
      if (isToday(endDate)) {
        return true;
      }
    }
    if (item.assignDate?.length === 2 && item.assignDate?.[0] && item.assignDate?.[1]) {
      const endDate = parseISO(item.assignDate[1]);
      if (isToday(endDate)) {
        return true;
      }
    }
    return false;
  }, [item.assignDate]);

  const handleMenuItemClick = (e) => {
    if (e.value === 'delete' && item.id) {
      setIsDeletePromptOpen(true);
    } else if (e.value === 'open') {
      router.replace(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}&viewId=${router.query.viewId}&taskId=${item.id}`,
        '',
        {
          shallow: true,
        }
      );
    } else if (e.value === 'move') {
      onChangeCurrentSelectedItem({
        isOpen: true,
        columnId: columnId,
        itemIndex: itemIndex,
        mode: 'move',
        item: item,
      });
      Analytics.track('Task moved', {
        'Board Name': boardData.name,
        'Task ID': item.id!,
      });
    } else if (e.value === 'duplicate') {
      setIsDuplicatePromptOpen(true);
    }
  };

  const handleDeleteTask = () => {
    if (item.id) {
      onDeleteItem({
        columnId: columnId,
        itemId: item.id,
        itemIndex: itemIndex,
      });
      Analytics.track('Board task deleted', {
        'Board name': boardData.name,
        'Task ID': item.id,
      });
      fetchTimers();
    }
  };

  const handleDuplicateTask = () => {
    if (item.id) {
      item.id && onDuplicateTask({ columnId: columnId, itemId: item.id });
      Analytics.track('Board task duplicated', {
        'Board name': boardData.name,
        'Task ID': item.id,
      });
      setIsDuplicatePromptOpen(false);
    }
    openSnackbar({
      snackType: 'standard',
      position: 'bottom-center',
      duration: 1500,
      text: 'Task is duplicated',
    });
  };

  const linkHref = useMemo(() => {
    let link = '';
    if (!templateId) {
      if (boardData.isPublicView) {
        //public
        link = `/${mode}/${orgId}/files/${router.query.slug}/public?view=${router.query.view}&type=${router.query.type}&viewId=${router.query.viewId}&taskId=${item.id}`;
      } else {
        link = `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}&viewId=${router.query.viewId}&taskId=${item.id}`;
      }
    }
    return link;
  }, [router.query.slug]);

  useEffect(() => {
    if (item.attachments?.length) {
      const image = item.attachments.find((img) =>
        /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(img.name)
      );
      if (image) {
        setCoverImage(image.url);
      }
    } else {
      setCoverImage('');
    }
  }, [item]);

  const completedTasks = useMemo(() => {
    return item.subtasks?.filter((t) => t.completed).length || 0;
  }, [item.subtasks]);

  /* Check if string is valid UUID */
  function checkIfValidUUID(str) {
    // Regular expression to check if string is a valid UUID
    const regexExp =
      /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

    return regexExp.test(str);
  }

  const getRepeatLabel = {
    weekly: 'Repeats weekly',
    monthly: 'Repeats monthly',
    yearly: 'Repeats yearly',
    custom: 'Repeats custom',
  };

  const epic = epics.find((epic) => epic._id === item.epic);

  const taskLabels = React.useMemo(() => {
    const label = item?.label ? castArray(item.label) : [];

    return label
      .map((item) => {
        const labelObj = boardData.labels.find((obj) => obj.id === item.id);

        return labelObj;
      })
      .filter(Boolean);
  }, [item?.label, boardData.labels]);

  return (
    <div className="relative">
      <>
        <Draggable
          draggableId={item.id || itemIndex.toString()}
          index={itemIndex}
          isDragDisabled={isReadOnly || boardData.access === 'view' || isLoading}
        >
          {(provided) => (
            <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
              <div
                style={{ boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.10)' }}
                className={cn(
                  {
                    'opacity-50': item?.completed && !isSelected,
                    'border-2 border-primary-300': isSelected,
                    'p-2': !isSelected && !item?.approvalStatus,
                  },
                  'w-full relative rounded-md cursor-pointer text-left bg-white kanban-card group'
                )}
                onMouseOver={() => {
                  setMouseOver(true);
                }}
                onMouseLeave={() => {
                  setMouseOver(true);
                }}
              >
                {!isReadOnly && boardData.access !== 'view' && (
                  <div
                    className={cn(
                      'absolute right-12 top-12 bg-white rounded pl-7 pr-4 kanban-more-action text-right w-70',
                      { hidden: !isMoreMenuOpened }
                    )}
                  >
                    <MoreActionMenu
                      onCloseMenu={onCloseMoreMenu}
                      onOpenMenu={onOpenMoreMenu}
                      usePortal
                      onClickMenuItem={handleMenuItemClick}
                      classNames={{
                        menuWrap: 'rounded-lg shadow-sm',
                      }}
                      menuItems={[
                        {
                          label: (
                            <div className="flex font-regular text-gray-999 text-sm ">
                              <EditIcon wrapperClass="mt-4 text-gray-500" />
                              &nbsp;&nbsp; Open task
                            </div>
                          ),
                          value: 'open',
                        },
                        {
                          label: (
                            <div className="flex font-regular text-gray-999 text-sm ">
                              <DuplicateIcon wrapperClass="mt-4 text-gray-500" />
                              &nbsp;&nbsp; Duplicate task
                            </div>
                          ),
                          value: 'duplicate',
                        },
                        {
                          label: (
                            <div className="flex font-regular text-gray-999 text-sm ">
                              <DeleteIcon wrapperClass="mt-4 text-gray-500" />
                              &nbsp;&nbsp; Delete
                            </div>
                          ),
                          value: 'delete',
                        },
                      ]}
                    />
                  </div>
                )}
                {checkIfValidUUID(item.id) && (
                  <div className="absolute w-full h-full rounded-md align-center">
                    <LoaderXs className="mx-auto my-15 text-xss"></LoaderXs>
                  </div>
                )}
                <Link href={linkHref} replace>
                  <a className={`${templateId ? 'disabled' : ''}`}>
                    <div className="p-10">
                      <span className="flex flex-row">
                        <span>{item.priority && renderPriorityBars[item.priority]}</span>
                        <div className="flex flex-row w-full gap-6 mb-4">
                          <span className="flex text-sm text-gray-500">
                            {boardData.key}#{item.number}
                          </span>
                          {!isReadOnly && (
                            <span
                              className={cn(
                                { 'opacity-100': isSelected },
                                'group-hover:opacity-100 opacity-0 flex items-center'
                              )}
                            >
                              <Tooltip
                                position="top center"
                                title="Select tasks to delete or move to another file"
                              >
                                <span>
                                  <CheckboxKanbanItem
                                    checked={isSelected}
                                    onChange={() =>
                                      item.id && boardData.toggleSelectedItem(item.id)
                                    }
                                  />
                                </span>
                              </Tooltip>
                            </span>
                          )}
                        </div>
                      </span>
                      <span className="flex">
                        <div
                          className={cn('text-sm mb-10 flex truncate-text', {
                            'text-gray-500 ': !item.name,
                          })}
                        >
                          {getFormattedValue(item.name) || 'Untitled'}
                        </div>
                      </span>

                      {coverImage && (
                        <Img
                          src={coverImage}
                          loader={<LoaderXs className="m-auto my-16" />}
                          className={cn(
                            'w-full object-cover rounded-md mb-10 transition-all duration-75',
                            {
                              'h-0': !isMouseOver,
                              'h-70': isMouseOver,
                            }
                          )}
                        />
                      )}
                      {item?.recurring?.type && (
                        <div className="flex text-gray-500 text-xs mb-4">
                          <span>
                            <ReloadIcon wrapperClass="text-gray-500 mt-4 mr-2 inline-block" />
                          </span>
                          <span className="inline-block">
                            {getRepeatLabel[item?.recurring?.type]}
                          </span>
                        </div>
                      )}

                      {item.assignDate?.length === 2 ? (
                        <span className={cn('text-xss text-gray-500 flex mb-10 font-normal')}>
                          <span className="flex flex-row leading-22 text-xs items-center">
                            {formatDateNew(item.assignDate, isEndDateToday)[0]}
                            <RightArrowIcon wrapperClass="flex text-gray-500 mx-8" size={10} />
                            {hasEndDatePassed || isEndDateToday ? (
                              <span className="flex text-red-600">
                                {formatDateNew(item.assignDate, isEndDateToday)[1]}
                              </span>
                            ) : (
                              formatDateNew(item.assignDate, isEndDateToday)[1]
                            )}
                          </span>
                        </span>
                      ) : item.assignDate?.length === 1 ? (
                        <span className="flex flex-row leading-22 text-xs text-gray-500 items-center mb-10 font-normal w-full">
                          {formatDateNew(item.assignDate, isEndDateToday) === 'Today' ? (
                            <span className="flex text-red-600">Today</span>
                          ) : hasEndDatePassed ? (
                            <span className="flex text-red-600">
                              {formatDateNew(item.assignDate, isEndDateToday)}
                            </span>
                          ) : (
                            formatDateNew(item.assignDate, isEndDateToday)
                          )}
                        </span>
                      ) : null}
                      {epic && (
                        <div className="flex flex-row gap-x-4 mb-16 items-center">
                          {epicLabel(epic)}
                        </div>
                      )}
                      {!!taskLabels.length && (
                        <div className="flex flex-row gap-x-4 mb-16 items-center">
                          {taskLabels.length > 3
                            ? taskLabels.slice(0, 3).map((obj, index) => (
                                <div
                                  key={index}
                                  style={{ backgroundColor: obj.color }}
                                  className="flex h-18 rounded-sm w-65 font-regular text-xss items-center px-6 truncate"
                                >
                                  {obj.label}
                                </div>
                              ))
                            : taskLabels.map((obj, index) => (
                                <div
                                  key={index}
                                  style={{ backgroundColor: obj.color }}
                                  className="flex h-18 rounded-sm w-65 font-regular text-xss items-center px-6 truncate"
                                >
                                  {obj.label}
                                </div>
                              ))}
                          {taskLabels.length > 3 ? (
                            <div className="flex ml-4 h-18 text-xs items-center ">
                              + {taskLabels.length - 3}
                            </div>
                          ) : null}
                        </div>
                      )}

                      {item.subtaskCount !== undefined && item.subtaskCount > 0 && (
                        <div className="flex flex-row text-gray-600 text-sm items-center mb-10">
                          <SubtaskIcon className="mr-2" />
                          <span className="leading-none ml-2 mr-9">
                            {completedTasks || 0}/{item.subtaskCount || 0} Subtasks
                          </span>
                        </div>
                      )}
                      <div className="max-h-120 overflow-y-auto">
                        {item.subtaskCount !== undefined &&
                          item.subtaskCount > 0 &&
                          item.subtasks?.map((subtask, index) => {
                            return (
                              <div
                                key={subtask.id}
                                className={cn('flex flex-row items-center justify-between py-10', {
                                  'border-b':
                                    index === item.subtaskCount && item.subtaskCount - 1
                                      ? false
                                      : true,
                                })}
                              >
                                <div className="flex text-gray-500 text-sm">
                                  {getFormattedValue(subtask.name) || 'Untitled'}
                                </div>
                                <div className="flex">
                                  <Tippy
                                    content="You do not have the required permission to edit tasks"
                                    disabled={!isReadOnly}
                                  >
                                    <span>
                                      <CheckboxMarkComplete
                                        checked={subtask?.completed}
                                        isDisabled={subtask.isLoading || isReadOnly}
                                        onChange={(e) => {
                                          if (item.id && subtask.id) {
                                            onMarkAsCompleteSubTask({
                                              parentTaskId: item.id,
                                              taskId: subtask.id,
                                              completed: e,
                                            });
                                          }
                                        }}
                                      />
                                    </span>
                                  </Tippy>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      <div className="flex border-t py-8 items-center">
                        <div className="flex flex-row w-full items-center justify-between">
                          <span className="flex">
                            {item.assignee?.length ? (
                              <div className="flex mr-12">
                                <AvatarGroup maxCount={1}>
                                  {item.assignee.map((assignee) => {
                                    return (
                                      <Avatar
                                        name={cn(assignee.firstname, assignee.lastname)}
                                        src={assignee.avatar}
                                        type="photo"
                                        key={assignee.memberId}
                                        size="md"
                                        wrapperClassName="-mr-8 border-2 border-white"
                                      />
                                    );
                                  })}
                                </AvatarGroup>
                                {item.assignee.length > 2 && (
                                  <Avatar
                                    name={item.assignee.length - 2}
                                    tooltip={false}
                                    type={'initials'}
                                    src={''}
                                    size="md"
                                    wrapperClassName="-ml-10 border-white border-1 transform transition duration-500 hover:scale-125"
                                  />
                                )}
                              </div>
                            ) : null}
                          </span>
                          <span className="flex items-center">
                            {!checkIfValidUUID(item.id) && (
                              <Tippy
                                content="You do not have the required permission to edit tasks"
                                disabled={!isReadOnly}
                              >
                                <span>
                                  <CheckboxMarkComplete
                                    checked={item?.completed}
                                    isDisabled={isReadOnly || boardData.access === 'view'}
                                    onChange={(e) => {
                                      onMarkAsComplete({
                                        columnId: columnId,
                                        itemId: item.id!,
                                        itemIndex: itemIndex,
                                        completed: e,
                                      });
                                    }}
                                  />
                                </span>
                              </Tippy>
                            )}
                            {item.attachments && item.attachments?.length > 0 && (
                              <div className="flex px-2 items-center text-sm ml-6">
                                <span className="pt-2">
                                  <CloudDownloadIcon className="text-gray-500 mr-2" />
                                </span>

                                <span className="text-gray-500 pb-2 ml-4">
                                  {item.attachments?.length}
                                </span>
                              </div>
                            )}
                            {getComment(item.comments) ? (
                              <div className="flex items-center px-2 text-sm ml-6">
                                <span className="pt-2">
                                  <CommentIcon className="text-gray-500 mr-2" />
                                </span>
                                <span className="text-gray-500 pb-2 ml-4">
                                  {getComment(item.comments)}
                                </span>
                              </div>
                            ) : null}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      className={cn(
                        'flex items-center px-12 overflow-hidden transition-all duration-75 pb-5',
                        {
                          'h-0': !isMouseOver,
                          'pt-6': isMouseOver,
                        }
                      )}
                    >
                      {item.estimates && item.estimates?.length > 0 && (
                        <div className="flex items-center pb-5">
                          {item.estimates[0].estimate?.text &&
                            item.estimates[0].estimate?.text.length > 0 && (
                              <>
                                <TimeHourglassIcon size={12} className="text-gray-500 mr-4" />
                                <span className=" text-gray-500 text-sm">
                                  {item.estimates[0].estimate?.text}
                                </span>
                              </>
                            )}
                        </div>
                      )}

                      {/* {boardData.timer?.isRunning && boardData.timer?.task === item.id && (
                        <div
                          className={cn('flex items-center w-100', {
                            'justify-center mx-10 pl-10 border-l':
                              item.estimates && item.estimates?.length > 0,
                          })}
                        >
                          <StopCircle className="mr-4 cursor-pointer hover:opacity-75" size={16} />
                          <TimerTracker
                            className="text-xs w-70 text-gray-500"
                            startTime={boardData.timer.startTime}
                            isRunning={boardData.timer.isRunning}
                            timeTrackedTaskId={boardData.timer.task}
                          />
                        </div>
                      )} */}
                      {/* {item?.timeLog && item?.timeLog?.length > 0 && !boardData.timer?.isRunning && (
                        <Tippy
                          content={`Time tracked for this task: ${calculateTaskTime(
                            item,
                            'string'
                          )}`}
                          className="w-auto h-auto bg-gray-999 tippy text-sm text-center"
                          placement="bottom"
                        >
                          <div
                            className={cn('flex text-gray-500 text-sm', {
                              'px-10 mx-10 border-l': item.estimates && item.estimates?.length > 0,
                            })}
                          >
                            <span className="mt-3 pr-4 block">
                              <TrackedTimeClockIcon />
                            </span>
                            <span>{calculateTaskTime(item, 'clock')}</span>
                          </div>
                        </Tippy>
                      )} */}
                    </div>

                    <div>
                      {item?.approvalStatus && (
                        <div
                          className={`${
                            ApprovalTagColor[item?.approvalStatus]
                          } px-8 py-4 w-full text-xs rounded-b-md capitalize font-medium`}
                        >
                          {ApprovalMessage[item?.approvalStatus]}
                        </div>
                      )}
                    </div>
                  </a>
                </Link>
              </div>
            </div>
          )}
        </Draggable>

        <Modal
          isOpen={isDeletePromptOpen}
          onClose={() => {
            setIsDeletePromptOpen(false);
          }}
        >
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsDeletePromptOpen(false);
                }}
                className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
              >
                Cancel
              </button>
              <button
                className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                onClick={handleDeleteTask}
              >
                Delete
              </button>
            </div>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isDuplicatePromptOpen}
          onClose={() => {
            setIsDuplicatePromptOpen(false);
          }}
        >
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsDuplicatePromptOpen(false);
                }}
                className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
              >
                Cancel
              </button>
              <button
                className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                onClick={handleDuplicateTask}
              >
                Duplicate
              </button>
            </div>
          </ModalContent>
        </Modal>
      </>
    </div>
  );
};

const stateSelector = (s: KanbanState) => ({
  columns: s.columns,
  onChangeCurrentSelectedItem: s.onChangeCurrentSelectedItem,
  isReadOnly: s.isReadOnly,
  onUpdateItemPlacement: s.onUpdateItemPlacement,
  onDeleteItem: s.onDeleteItem,
  onToggleTaskUpvote: s.onToggleTaskUpvote,
  onMarkAsComplete: s.onMarkAsComplete,
  onCloseCurrentSelectedItem: s.onCloseCurrentSelectedItem,
  onDuplicateTask: s.onDuplicateItem,
  isLoading: s.isLoading,
});

export const calculatePercentage = (total: number, value: number) =>
  parseFloat(((value / total) * 100).toFixed());

export function useCombinedRefs<T>(...refs: readonly React.Ref<T>[]) {
  return useCallback((handle: T | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(handle);
      } else if (ref !== null) {
        // @ts-expect-error: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31065
        ref.current = handle;
      }
    }
  }, refs);
}

export const formatDateLight = (dates) => {
  const dateFormat = 'MMM d, yyyy';
  if (dates.length === 2 && dates?.[0] && dates?.[1]) {
    if (isSameDay(new Date(dates[0]), new Date(dates[1]))) {
      return `${format(parseISO(dates[0]), dateFormat)} | ${format(
        parseISO(dates[0]),
        'hh:mm aa'
      )} - ${format(parseISO(dates[1]), 'hh:mm aa')}`;
    }
    return `${format(parseISO(dates[0]), dateFormat)} - ${format(parseISO(dates[1]), dateFormat)}`;
  } else if (dates.length === 1 && dates?.[0]) {
    return format(parseISO(dates[0]), dateFormat);
  }
  return '';
};

export const formatDateNew = (dates, isEndDateToday) => {
  const dateFormat = 'MMM d, yyyy';
  if (dates.length === 2 && dates?.[0] && dates?.[1]) {
    if (isEndDateToday) return [format(parseISO(dates[0]), dateFormat), 'Today'];
    else return [format(parseISO(dates[0]), dateFormat), format(parseISO(dates[1]), dateFormat)];
  } else if (dates.length === 1 && dates?.[0]) {
    const singleDate = format(parseISO(dates[0]), dateFormat);
    if (isToday(dates[0])) {
      return 'Today';
    } else {
      return singleDate;
    }
  }
  return '';
};
